import { useState, useCallback, useMemo, useEffect } from "react";
import { projectExportConfigSchema } from "@iamu/shared";
import { useCanEdit } from "@/lib/project-access";
import { useProject } from "@/components/project-context";
import { useWorkRequests } from "@/components/experience/use-work-requests";
import { useCopyWalkthroughPrompt } from "@/hooks/use-copy-walkthrough-prompt";
import type { DeltaItem } from "@/components/walkthrough/since-last-run";
import { enrichFindingsWithStepData } from "@/components/walkthrough/zen-mode";

import { useWorkspaceSearchParams } from "./use-workspace-search-params";
import { useBatchSelection } from "./use-batch-selection";
import { useWalkthroughWorkspaceData } from "./use-walkthrough-workspace-data";
import { useWalkthroughShare } from "./use-walkthrough-share";
import { useWorkspaceKeyboard } from "./use-workspace-keyboard";
import { useWorkspaceMutations } from "./use-workspace-mutations";

export interface UseWalkthroughWorkspaceOptions {
  projectId?: string;
  walkthroughId?: string;
  isValidId: boolean;
}

export function useWalkthroughWorkspace({
  projectId,
  walkthroughId,
  isValidId,
}: UseWalkthroughWorkspaceOptions) {
  const project = useProject();
  const hasLinkedRepo = !!project.githubRepositoryId;
  const workRequests = useWorkRequests(projectId ?? "");

  // Search Params
  const searchParams = useWorkspaceSearchParams();
  const {
    activeTab,
    setActiveTab,
    statusFilter: rawStatusFilter,
    hasExplicitStatusFilter,
    setStatusFilter,
    kindFilter,
    setKindFilter,
    severityFilter,
    setSeverityFilter,
    selectedFindingId,
    selectFinding,
    baseRunId,
    selectBaseRun,
    setReportOpen,
    stepNumber: stepNumberFromUrl,
    reportOpen,
  } = searchParams;

  // Workspace Data Hook
  const data = useWalkthroughWorkspaceData({
    projectId,
    walkthroughId,
    isValidId,
    baseRunId,
    selectedFindingId,
    stepNumberFromUrl,
    statusFilter: rawStatusFilter,
    hasExplicitStatusFilter,
    kindFilter,
    severityFilter,
  });

  const statusFilter = data.effectiveStatusFilter;

  const {
    walkthrough,
    filteredFindings,
    walkthroughSteps,
    setSelectedDeltaItem,
  } = data;

  // Batch Selection
  const selection = useBatchSelection();
  const {
    selectedIds,
    focusedIndex,
    toggle: toggleSelect,
    toggleAll: toggleSelectAll,
    clear: clearSelection,
    setFocusedIndex,
    moveFocus,
  } = selection;

  // Permissions & Integrations
  const canEdit = useCanEdit();
  const copyPrompt = useCopyWalkthroughPrompt({
    walkthroughId: walkthroughId ?? null,
    surface: "detail-page",
  });
  const activeProvider =
    data.trackerTarget?.provider ?? (hasLinkedRepo ? "github" : null);
  const isTrackerConfigured = Boolean(
    data.trackerTarget?.targetKey || hasLinkedRepo,
  );
  const exportConfig = useMemo(() => {
    if (!data.projectContext?.exportConfig) return undefined;
    try {
      return projectExportConfigSchema.parse(data.projectContext.exportConfig);
    } catch {
      return undefined;
    }
  }, [data.projectContext]);

  // Mutations and Dialogs
  const mutations = useWorkspaceMutations({
    projectId: projectId ?? "",
    walkthroughId,
    trackerTarget: data.trackerTarget,
    hasLinkedRepo,
    walkthrough,
    clearSelection,
  });

  // Sharing Hook
  const share = useWalkthroughShare({ walkthroughId, isValidId });

  // Finding Handlers
  const handleOpenFinding = useCallback(
    (targetId: string, item?: DeltaItem | null) => {
      selectFinding(targetId);
      setSelectedDeltaItem(item ?? null);
      const idx = filteredFindings.findIndex(
        (f) =>
          f.id === targetId ||
          f.canonicalFindingId === targetId ||
          (item && f.title && f.title.trim() === item.title.trim()),
      );
      setFocusedIndex(idx);
    },
    [filteredFindings, selectFinding, setSelectedDeltaItem, setFocusedIndex],
  );

  const handleCloseDetail = useCallback(() => {
    setFocusedIndex(-1);
    setSelectedDeltaItem(null);
    selectFinding(null);
  }, [setFocusedIndex, setSelectedDeltaItem, selectFinding]);

  // Zen Mode Carousel State
  const [zenModal, setZenModal] = useState<{ open: boolean; index: number }>({
    open: false,
    index: 0,
  });

  const handleOpenZenMode = useCallback((index = 0) => {
    setZenModal({ open: true, index });
  }, []);

  const zenFindings = useMemo(() => {
    return enrichFindingsWithStepData(
      filteredFindings,
      walkthroughSteps,
      walkthrough?.persona?.name,
      walkthrough?.id,
    );
  }, [
    filteredFindings,
    walkthroughSteps,
    walkthrough?.persona?.name,
    walkthrough?.id,
  ]);

  // Keyboard Shortcuts
  useWorkspaceKeyboard({
    filteredFindings,
    focusedIndex,
    selectedIds,
    isModalOpen: !!(mutations.actionDialog || reportOpen || zenModal.open),
    clearSelection,
    moveFocus,
    toggleSelect,
  });

  // Sync focused index with selected finding when moving via keyboard
  useEffect(() => {
    if (focusedIndex >= 0 && focusedIndex < filteredFindings.length) {
      const nextId = filteredFindings[focusedIndex]?.id;
      if (nextId && nextId !== selectedFindingId) {
        selectFinding(nextId);
        setSelectedDeltaItem(null);
      }
    }
  }, [
    focusedIndex,
    filteredFindings,
    selectedFindingId,
    selectFinding,
    setSelectedDeltaItem,
  ]);

  return {
    data,
    searchParams: {
      ...searchParams,
      statusFilter,
    },
    selection,
    canEdit,
    hasLinkedRepo,
    isTrackerConfigured,
    activeProvider,
    exportConfig,
    mutations,
    share,
    copyPrompt,
    workRequests,
    handlers: {
      handleOpenFinding,
      handleCloseDetail,
      handleOpenZenMode,
    },
    zen: {
      zenModal,
      setZenModal,
      zenFindings,
    },
  };
}
